import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, Palette } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const SecuritySettingsTab = ({
  securitySettings,
  handleSecurityChange,
  changePassword,
  handlePasswordChange,
  passwordData,
  changingPassword,
}) => {
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  const canEditSecurityConfig = canEdit("settings_security_configuration");
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* ---------------- SECURITY SETTINGS CARD ---------------- */}
      <Card id="setting-ui-security">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Security Settings</span>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* 2FA */}
          <div
            className="flex items-center justify-between"
            id="setting-security-2fa"
          >
            <Label>Two-Factor Authentication</Label>
            <Switch
              disabled={!canEditSecurityConfig}
              checked={securitySettings.two_factor_auth}
              onCheckedChange={(checked) =>
                handleSecurityChange("two_factor_auth", checked)
              }
            />
          </div>

          {/* Password Policy */}
          <div id="setting-ui-Password-Policy">
            <Label>Password Policy</Label>
            <Select
              disabled={!canEditSecurityConfig}
              value={securitySettings.password_policy}
              onValueChange={(value) =>
                handleSecurityChange("password_policy", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select password complexity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low (6+ characters)</SelectItem>
                <SelectItem value="medium">
                  Medium (8+ chars, mixed case)
                </SelectItem>
                <SelectItem value="high">
                  High (12+ chars, mixed case, special characters)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Session Timeout */}
          <div id="setting-security-timeout">
            <Label>Session Timeout (minutes)</Label>
            <Input
              disabled={!canEditSecurityConfig}
              onWheel={(e) => e.target.blur()}
              type="number"
              min="5"
              max="480"
              placeholder="e.g., 30"
              value={securitySettings.session_timeout}
              onChange={(e) =>
                handleSecurityChange(
                  "session_timeout",
                  parseInt(e.target.value),
                )
              }
            />
          </div>

          {/* Audit Logging */}
          <div id="setting-Audit" className="flex items-center justify-between">
            <Label>Audit Logging</Label>
            <Switch
              disabled={!canEditSecurityConfig}
              checked={securitySettings.audit_logging}
              onCheckedChange={(checked) =>
                handleSecurityChange("audit_logging", checked)
              }
            />
          </div>

          {/* Encryption */}
          <div
            id="setting-Encryption"
            className="flex items-center justify-between"
          >
            <Label>Data Encryption</Label>
            <Switch
              disabled={!canEditSecurityConfig}
              checked={securitySettings.data_encryption}
              onCheckedChange={(checked) =>
                handleSecurityChange("data_encryption", checked)
              }
            />
          </div>

          {/* Backup Frequency */}
          <div id="setting-advanced-backup">
            <Label>Backup Frequency</Label>
            <Select
              disabled={!canEditSecurityConfig}
              value={securitySettings.backup_frequency}
              onValueChange={(value) =>
                handleSecurityChange("backup_frequency", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ---------------- CHANGE PASSWORD CARD ---------------- */}
      <div id="setting-security-password">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Change Password</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                disabled={!canEditSecurityConfig}
                id="current_password"
                type="password"
                placeholder="Enter current password"
                value={passwordData.current_password}
                onChange={(e) =>
                  handlePasswordChange("current_password", e.target.value)
                }
              />
            </div>

            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                disabled={!canEditSecurityConfig}
                id="new_password"
                type="password"
                placeholder="Enter new password"
                value={passwordData.new_password}
                onChange={(e) =>
                  handlePasswordChange("new_password", e.target.value)
                }
              />
            </div>

            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                disabled={!canEditSecurityConfig}
                id="confirm_password"
                type="password"
                placeholder="Re-enter new password"
                value={passwordData.confirm_password}
                onChange={(e) =>
                  handlePasswordChange("confirm_password", e.target.value)
                }
              />
            </div>

            <Button
              onClick={() => {
                if (!canEdit("settings_security_configuration", true)) return;
                changePassword();
              }}
              disabled={
                changingPassword ||
                !passwordData.current_password ||
                !passwordData.new_password ||
                !passwordData.confirm_password
              }
              className="w-full"
            >
              {changingPassword ? "Changing Password..." : "Change Password"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecuritySettingsTab;
